import React, { useState, useEffect } from "react";
import { generateLectureNotes, uploadFiles } from "../api";
import { getApiBase } from "../apiBase";
import { C as TOKENS } from "../constants";
import PageHeader from "../components/PageHeader";
import { getPlannerContext, plannerContextBanner } from "../utils/plannerContext";
import { formatApiError } from "../utils/apiError";

// Align with Schedule Planner blue tokens
const C = {
  primary: TOKENS.primary,
  bg: TOKENS.bg,
  surface: TOKENS.surface,
  border: TOKENS.border,
  error: TOKENS.error,
  errorBg: TOKENS.errorBg,
  success: TOKENS.success,
  successBg: TOKENS.successBg,
  muted: TOKENS.muted,
  text: TOKENS.text,
  primarySoft: TOKENS.primarySoft,
  primaryDark: TOKENS.primaryDark,
  warning: TOKENS.warning,
  warningBg: TOKENS.warningBg,
};

const TYPES = [
  { key: "notes",      label: "📖 Lecture Notes",  desc: "Theory, formulas, worked example" },
  { key: "activity",   label: "🎮 Activity",        desc: "Classroom exercise with rubric" },
  { key: "worksheet",  label: "📝 Worksheet",       desc: "Student practice questions" },
  { key: "assessment", label: "📊 Assessment",      desc: "MCQ + short + long answer quiz" },
  { key: "all",        label: "⚡ Generate All",    desc: "All 4 types at once" },
];

// ── Download helper ───────────────────────────────────────────────────────────
function downloadAsText(result) {
  const lines = [];
  lines.push(`LECTURE CONTENT — ${result.subject} | ${result.topic}`);
  lines.push("=".repeat(60));

  if (result.lectureNotes) {
    const n = result.lectureNotes;
    lines.push("\n📖 LECTURE NOTES");
    lines.push("-".repeat(40));
    if (n.explanation)           lines.push("Explanation:\n" + n.explanation);
    if (n.definitions?.length)   lines.push("\nDefinitions:\n" + n.definitions.map(d => "• " + d).join("\n"));
    if (n.formula)               lines.push("\nFormula / Rule:\n" + n.formula);
    if (n.workedExample)         lines.push("\nWorked Example:\n" + n.workedExample);
    if (n.commonMistakes?.length) lines.push("\nCommon Mistakes:\n" + n.commonMistakes.map(m => "• " + m).join("\n"));
    if (n.realWorldApplication)  lines.push("\nReal-World Application:\n" + n.realWorldApplication);
  }

  if (result.activity) {
    const a = result.activity;
    lines.push("\n\n🎮 CLASSROOM ACTIVITY");
    lines.push("-".repeat(40));
    if (a.name)               lines.push("Activity: " + a.name);
    if (a.objective)          lines.push("Objective: " + a.objective);
    if (a.duration)           lines.push("Duration: " + a.duration + " minutes");
    if (a.instructions?.length) lines.push("\nInstructions:\n" + a.instructions.map((s, i) => (i + 1) + ". " + s).join("\n"));
    if (a.materials?.length)  lines.push("\nMaterials: " + a.materials.join(", "));
    if (a.learningOutcome)    lines.push("\nLearning Outcome: " + a.learningOutcome);
    if (a.rubric?.length) {
      lines.push("\nRubric:");
      a.rubric.forEach(r => lines.push(`  ${r.criterion}: ${r.excellent} | ${r.satisfactory} | ${r.needsWork}`));
    }
  }

  if (result.worksheet) {
    const w = result.worksheet;
    lines.push("\n\n📝 WORKSHEET");
    lines.push("-".repeat(40));
    if (w.shortAnswerQuestions?.length) lines.push("Short Answer:\n" + w.shortAnswerQuestions.map((q, i) => (i + 1) + ". " + q).join("\n"));
    if (w.problemSolving)               lines.push("\nProblem Solving:\n" + w.problemSolving);
    if (w.applicationQuestion)          lines.push("\nApplication Question:\n" + w.applicationQuestion);
    if (w.hints?.length)                lines.push("\nHints:\n" + w.hints.map(h => "• " + h).join("\n"));
  }

  if (result.assessment) {
    const a = result.assessment;
    lines.push("\n\n📊 ASSESSMENT");
    lines.push("-".repeat(40));
    if (a.mcq?.length) {
      lines.push("MCQ:");
      a.mcq.forEach((q, i) => {
        lines.push(`Q${i + 1}. ${q.question}`);
        (q.options || []).forEach((opt, j) => lines.push(`  ${["A","B","C","D"][j]}. ${opt}${j === q.correct ? " ✓" : ""}`));
      });
    }
    if (a.shortAnswer?.length) lines.push("\nShort Answer:\n" + a.shortAnswer.map((q, i) => (i + 1) + ". " + q).join("\n"));
    if (a.longAnswer)          lines.push("\nLong Answer:\n" + a.longAnswer);
    if (a.markingScheme)       lines.push("\nMarking Scheme:\n" + a.markingScheme);
  }

  const blob = new Blob([lines.join("\n")], { type: "text/plain" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${result.subject}_${result.topic}_content.txt`.replace(/[^a-zA-Z0-9._-]/g, "_");
  a.click();
  URL.revokeObjectURL(url);
}

// ── Shared UI primitives ──────────────────────────────────────────────────────
function Card({ title, children, color = C.primary, bg = "#E8F0FF", defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ padding: "12px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between",
          alignItems: "center", background: open ? bg : C.bg, fontWeight: 700, fontSize: 14,
          color: open ? color : C.text, userSelect: "none" }}>
        {title}
        <span style={{ fontSize: 13, opacity: 0.7 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && <div style={{ padding: 16, background: C.surface }}>{children}</div>}
    </div>
  );
}

function SectionHeader({ children, color = C.primary }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase",
      letterSpacing: "0.06em", marginBottom: 8, marginTop: 4,
      borderBottom: `1px solid ${C.border}`, paddingBottom: 4 }}>
      {children}
    </div>
  );
}

function Pill({ children, color = C.primary, bg = "#E8F0FF" }) {
  return (
    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 20,
      background: bg, color, fontSize: 11, fontWeight: 700, marginRight: 6, marginBottom: 4 }}>
      {children}
    </span>
  );
}

// Renders a labelled block — strings as pre-wrap, arrays as bullet list
function Field({ label, value, bg = C.bg }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      {label && (
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase",
          letterSpacing: "0.05em", marginBottom: 5 }}>{label}</div>
      )}
      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.75, background: bg,
        padding: "10px 14px", borderRadius: 8, whiteSpace: "pre-wrap" }}>
        {Array.isArray(value)
          ? value.map((v, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 4 }}>
              <span style={{ color: C.primary, fontWeight: 700, minWidth: 14 }}>•</span>
              <span>{typeof v === "string" ? v : JSON.stringify(v)}</span>
            </div>
          ))
          : String(value)
        }
      </div>
    </div>
  );
}

// ── 📖 Lecture Notes section ──────────────────────────────────────────────────
function LectureNotesSection({ notes }) {
  if (!notes) return null;
  return (
    <Card title="📖 Lecture Notes" color="#059669" bg="#ECFDF5" defaultOpen={true}>

      {notes.explanation && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader color="#059669">Core Concept</SectionHeader>
          <p style={{ fontSize: 13, color: C.text, lineHeight: 1.8, margin: 0,
            background: "#ECFDF5", padding: "10px 14px", borderRadius: 8 }}>
            {notes.explanation}
          </p>
        </div>
      )}

      {notes.definitions?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader color="#059669">Key Definitions</SectionHeader>
          {notes.definitions.map((def, i) => (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8,
              background: C.bg, borderRadius: 8, padding: "10px 12px" }}>
              <span style={{ fontWeight: 700, color: C.primary, fontSize: 13, minWidth: 20 }}>{i + 1}.</span>
              <span style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>{def}</span>
            </div>
          ))}
        </div>
      )}

      {notes.formula && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader color="#059669">Formula / Rule</SectionHeader>
          <div style={{ background: "#1E293B", color: "#E2E8F0", fontFamily: "monospace",
            fontSize: 13, padding: "12px 16px", borderRadius: 8, whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
            {notes.formula}
          </div>
        </div>
      )}

      {notes.workedExample && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader color="#059669">Worked Example</SectionHeader>
          <div style={{ background: "#E7F0F6", border: "1px solid #7BA7C9",
            borderRadius: 8, padding: "12px 14px", fontSize: 13, color: "#78350F",
            lineHeight: 1.75, whiteSpace: "pre-wrap" }}>
            {notes.workedExample}
          </div>
        </div>
      )}

      {notes.commonMistakes?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader color="#059669">Common Mistakes</SectionHeader>
          {notes.commonMistakes.map((m, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6,
              background: "#FEF2F2", border: "1px solid #FECACA",
              borderRadius: 8, padding: "8px 12px" }}>
              <span style={{ color: C.error, fontWeight: 700, fontSize: 14 }}>⚠</span>
              <span style={{ fontSize: 13, color: "#7F1D1D" }}>{m}</span>
            </div>
          ))}
        </div>
      )}

      {notes.realWorldApplication && (
        <div>
          <SectionHeader color="#059669">Real-World Application</SectionHeader>
          <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE",
            borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#1E40AF", lineHeight: 1.75 }}>
            🌐 {notes.realWorldApplication}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── 🎮 Classroom Activity section ────────────────────────────────────────────
function ActivitySection({ activity }) {
  if (!activity) return null;
  return (
    <Card title="🎮 Classroom Activity" color="#1B4F72" bg="#E7F0F6">

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        {activity.name     && <Pill color="#1B4F72" bg="#E7F0F6">{activity.name}</Pill>}
        {activity.duration && <Pill color="#059669" bg="#ECFDF5">⏱ {activity.duration} min</Pill>}
      </div>

      {activity.objective && (
        <div style={{ marginBottom: 14 }}>
          <SectionHeader color="#1B4F72">Objective</SectionHeader>
          <p style={{ fontSize: 13, color: C.text, lineHeight: 1.7, margin: 0,
            background: "#E7F0F6", padding: "10px 12px", borderRadius: 8 }}>
            {activity.objective}
          </p>
        </div>
      )}

      {activity.instructions?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <SectionHeader color="#1B4F72">Instructions</SectionHeader>
          {activity.instructions.map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "flex-start" }}>
              <div style={{ minWidth: 24, height: 24, background: "#1B4F72", color: "#fff",
                borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                {i + 1}
              </div>
              <span style={{ fontSize: 13, color: C.text, lineHeight: 1.6, paddingTop: 2 }}>{step}</span>
            </div>
          ))}
        </div>
      )}

      {activity.materials?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <SectionHeader color="#1B4F72">Materials Needed</SectionHeader>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {activity.materials.map((m, i) => (
              <span key={i} style={{ background: C.bg, border: `1px solid ${C.border}`,
                padding: "4px 10px", borderRadius: 6, fontSize: 12, color: C.text }}>
                📦 {m}
              </span>
            ))}
          </div>
        </div>
      )}

      {activity.learningOutcome && (
        <div style={{ marginBottom: 14 }}>
          <SectionHeader color="#1B4F72">Learning Outcome</SectionHeader>
          <div style={{ background: "#ECFDF5", border: "1px solid #6EE7B7",
            borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "#065F46" }}>
            🎯 {activity.learningOutcome}
          </div>
        </div>
      )}

      {activity.rubric?.length > 0 && (
        <div>
          <SectionHeader color="#1B4F72">Assessment Rubric</SectionHeader>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  {["Criterion", "Excellent", "Satisfactory", "Needs Work"].map(h => (
                    <th key={h} style={{ padding: "8px 10px", textAlign: "left",
                      border: `1px solid ${C.border}`, fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activity.rubric.map((r, i) => (
                  <tr key={i}>
                    <td style={{ padding: "8px 10px", border: `1px solid ${C.border}`, fontWeight: 600 }}>{r.criterion}</td>
                    <td style={{ padding: "8px 10px", border: `1px solid ${C.border}`, color: C.success }}>{r.excellent}</td>
                    <td style={{ padding: "8px 10px", border: `1px solid ${C.border}`, color: "#1B4F72" }}>{r.satisfactory}</td>
                    <td style={{ padding: "8px 10px", border: `1px solid ${C.border}`, color: C.error }}>{r.needsWork}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Helper: safely extract question text + optional answer from AI output ─────
// The AI sometimes returns objects like {question, steps} or {question, answer}
// instead of plain strings. This normalises both shapes.
function extractQuestion(q) {
  if (!q) return { text: "", answer: null };

  if (typeof q === "string") {
    return { text: q, answer: null };
  }

  if (typeof q === "object") {
    return {
      text:
        q.question ||
        q.text ||
        q.q ||
        "",

      answer:
        q.answer ||
        q.modelAnswer ||
        q.solution ||
        q.correctAnswer ||
        q.explanation ||
        q.sampleAnswer ||
        null
    };
  }

  return { text: String(q), answer: null };
}

// ── 📝 Worksheet section ──────────────────────────────────────────────────────
function WorksheetSection({ worksheet }) {
  const [showHints, setShowHints] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  if (!worksheet) return null;

  const { shortAnswerQuestions = [], problemSolving, applicationQuestion, hints = [] } = worksheet;
  if (!shortAnswerQuestions.length && !problemSolving && !applicationQuestion) return null;

  // Check if any question has an embedded answer to offer a toggle
 const hasAnswers =
  shortAnswerQuestions.some(q => !!extractQuestion(q).answer) ||
  !!extractQuestion(problemSolving).answer ||
  !!extractQuestion(applicationQuestion).answer;
  
  return (
    <Card title="📝 Worksheet" color="#0B5FFF" bg="#E8F0FF">

      {/* Toggle buttons row */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
        {hasAnswers && (
          <button
            onClick={() => setShowAnswers(a => !a)}
            style={{ fontSize: 12, padding: "6px 14px", background: showAnswers ? "#0B5FFF" : "#E8F0FF",
              color: showAnswers ? "#fff" : "#0B5FFF", border: "1px solid #3D7EFF",
              borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
            {showAnswers ? "🙈 Hide Answers" : "🗝 Show Answers"}
          </button>
        )}
        {hints.length > 0 && (
          <button
            onClick={() => setShowHints(h => !h)}
            style={{ fontSize: 12, padding: "6px 14px", background: showHints ? "#0B5FFF" : "#E8F0FF",
              color: showHints ? "#fff" : "#0B5FFF", border: "1px solid #3D7EFF",
              borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
            {showHints ? "🙈 Hide Hints" : "💡 Show Hints"}
          </button>
        )}
      </div>

      {shortAnswerQuestions.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader color="#0B5FFF">Short Answer Questions</SectionHeader>
          {shortAnswerQuestions.map((q, i) => {
            const { text, answer } = extractQuestion(q);
            return (
              <div key={i} style={{ marginBottom: 10, background: "#fff",
                border: "1px solid #DDD6FE", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ display: "flex", gap: 10, padding: "10px 14px" }}>
                  <span style={{ fontWeight: 700, color: "#0B5FFF", minWidth: 24, fontSize: 13 }}>Q{i + 1}.</span>
                  <span style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>{text}</span>
                </div>
                {showAnswers && answer && (
                  <div style={{ borderTop: "1px solid #DDD6FE", padding: "8px 14px",
                    background: "#ECFDF5", fontSize: 13, color: "#065F46", lineHeight: 1.6 }}>
                    <span style={{ fontWeight: 700 }}>✓ Answer: </span>{answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {problemSolving && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader color="#0B5FFF">Problem Solving</SectionHeader>
          <div style={{ background: "#fff", border: "1px solid #DDD6FE",
            borderRadius: 8, padding: "10px 14px", fontSize: 13, color: C.text, lineHeight: 1.7 }}>
            🔧 {typeof problemSolving === "string" ? problemSolving : extractQuestion(problemSolving).text}
          </div>
        </div>
      )}

      {applicationQuestion && (
        <div style={{ marginBottom: hints.length && !showHints ? 0 : 16 }}>
          <SectionHeader color="#0B5FFF">Application / Real-World Question</SectionHeader>
          {(() => {
            const { text, answer } = extractQuestion(applicationQuestion);
            return (
              <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE",
                borderRadius: 8, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px", fontSize: 13, color: "#1E40AF", lineHeight: 1.7 }}>
                  🌐 {text}
                </div>
                {showAnswers && answer && (
                  <div style={{ borderTop: "1px solid #BFDBFE", padding: "8px 14px",
                    background: "#ECFDF5", fontSize: 13, color: "#065F46", lineHeight: 1.6 }}>
                    <span style={{ fontWeight: 700 }}>✓ Answer: </span>{answer}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {showHints && hints.length > 0 && (
        <div>
          <SectionHeader color="#0B5FFF">Hints</SectionHeader>
          {hints.map((h, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6,
              background: "#E7F0F6", border: "1px solid #7BA7C9",
              borderRadius: 8, padding: "8px 12px" }}>
              <span style={{ color: "#1B4F72", fontWeight: 700 }}>💡</span>
              <span style={{ fontSize: 13, color: "#78350F" }}>
                {typeof h === "string" ? h : JSON.stringify(h)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── 📊 Assessment section ─────────────────────────────────────────────────────
function AssessmentSection({ assessment }) {
  const [showAnswers, setShowAnswers] = useState(false);
  if (!assessment) return null;

  const { mcq = [], shortAnswer = [], longAnswer, markingScheme } = assessment;
  if (!mcq.length && !shortAnswer.length && !longAnswer) return null;

  return (
    <Card title="📊 Assessment / Quiz" color="#DC2626" bg="#FEF2F2">

      

      {mcq.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader color="#DC2626">Multiple Choice Questions</SectionHeader>
          {mcq.map((q, i) => (
            <div key={i} style={{ background: C.bg, borderRadius: 8, padding: "12px 14px", marginBottom: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: C.text, marginBottom: 8 }}>
                Q{i + 1}. {q.question}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                {(q.options || []).map((opt, j) => {
                  const isCorrect = j === q.correct;
                  const show = showAnswers && isCorrect;
                  return (
                    <div key={j} style={{
                      display: "flex", gap: 6, padding: "6px 10px", borderRadius: 6, fontSize: 12,
                      background: show ? "#ECFDF5" : "#fff",
                      border: `1px solid ${show ? "#6EE7B7" : C.border}`,
                      color: show ? C.success : C.text,
                      fontWeight: show ? 700 : 400 }}>
                      <span style={{ fontWeight: 700, minWidth: 16 }}>{["A","B","C","D"][j]}.</span>
                      <span>{opt}</span>
                      {show && <span style={{ marginLeft: "auto" }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {shortAnswer.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader color="#DC2626">Short Answer Questions</SectionHeader>
          {shortAnswer.map((q, i) => {
            const { text, answer } = extractQuestion(q);
            return (
              <div key={i} style={{ marginBottom: 8, background: "#fff",
                border: "1px solid #FECACA", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ display: "flex", gap: 10, padding: "10px 14px" }}>
                  <span style={{ fontWeight: 700, color: "#DC2626", minWidth: 24, fontSize: 13 }}>Q{i + 1}.</span>
                  <span style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>{text}</span>
                </div>
                {showAnswers && answer && (
                  <div style={{ borderTop: "1px solid #FECACA", padding: "8px 14px",
                    background: "#ECFDF5", fontSize: 13, color: "#065F46", lineHeight: 1.6 }}>
                    <span style={{ fontWeight: 700 }}>✓ Answer: </span>{answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {longAnswer && (
        <div style={{ marginBottom: 14 }}>
          <SectionHeader color="#DC2626">Long Answer Question</SectionHeader>
          {typeof longAnswer === "object" ? (() => {
            const { text, answer } = extractQuestion(longAnswer);
            return (
              <div style={{ background: "#fff", border: "1px solid #FECACA", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ padding: "12px 14px", fontSize: 13, color: C.text, lineHeight: 1.7 }}>{text}</div>
                {showAnswers && answer && (
                  <div style={{ borderTop: "1px solid #FECACA", padding: "10px 14px",
                    background: "#ECFDF5", fontSize: 13, color: "#065F46", lineHeight: 1.7 }}>
                    <span style={{ fontWeight: 700 }}>✓ Model Answer: </span>{answer}
                  </div>
                )}
              </div>
            );
          })() : (
            <div style={{ background: "#fff", border: "1px solid #FECACA",
              borderRadius: 8, padding: "12px 14px", fontSize: 13, color: C.text, lineHeight: 1.7 }}>
              {longAnswer}
            </div>
          )}
        </div>
      )}

      {markingScheme && (
        <div>
          <SectionHeader color="#DC2626">Marking Scheme</SectionHeader>
          <div style={{ background: "#E7F0F6", border: "1px solid #7BA7C9",
            borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#78350F",
            lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            📋 {markingScheme}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LectureNotes() {
  const [subject,        setSubject]        = useState("");
  const [topic,          setTopic]          = useState("");
  const [syllabusText,   setSyllabusText]   = useState("");
  const [type,           setType]           = useState("all");
  const [loading,        setLoading]        = useState(false);
  const [result,         setResult]         = useState(null);
  const [error,          setError]          = useState("");
  const [uploading,      setUploading]      = useState(false);
  const [history,        setHistory]        = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory,    setShowHistory]    = useState(false);

  const getToken = () => localStorage.getItem("token");

  useEffect(() => {
    const ctx = getPlannerContext();
    setSubject((s) => s || ctx.subject || "");
    setTopic((t) => t || ctx.topicHint || "");
    setSyllabusText((sy) => sy || [ctx.syllabus, ctx.dsaSyllabus].filter(Boolean).join("\n\n"));
  }, []);

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    const token = getToken();
    if (!token) { setHistoryLoading(false); return; }
    setHistoryLoading(true);
    try {
      const res  = await fetch(`${getApiBase()}/lecture-notes`, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) setHistory(data.items || []);
    } catch (err) {
      console.error("Failed to load history:", err);
    }
    setHistoryLoading(false);
  };

  const loadSaved = async (sub, top) => {
    const token = getToken();
    if (!token) { setError("You must be logged in."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const res  = await fetch(
        `${getApiBase()}/lecture-notes/content?subject=${encodeURIComponent(sub)}&topic=${encodeURIComponent(top)}`,
        { headers: { Authorization: "Bearer " + token } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        setResult(data.content);
        setSubject(sub);
        setTopic(top);
        setShowHistory(false);
      } else setError(formatApiError(data.error, "Failed to load"));
    } catch (err) { setError("Failed to load: " + err.message); }
    setLoading(false);
  };

  const handleUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const res  = await uploadFiles({ syllabus: Array.from(files) });
      const text = (res.extracted?.syllabus || []).map(f => f.content).join("\n");
      setSyllabusText(text);
    } catch {}
    setUploading(false);
  };

  const handleGenerate = async () => {
    if (!subject.trim() || !topic.trim()) { setError("Subject and topic are required"); return; }
    const token = getToken();
    if (!token) { setError("You must be logged in to generate content."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const res = await generateLectureNotes(subject, topic, syllabusText, null, type);
      if (res.success) {
        setResult(res.content);
        await loadHistory();
      } else setError(formatApiError(res.error, "Generation failed"));
    } catch (err) { setError(formatApiError(err, "Generation failed")); }
    setLoading(false);
  };

  const deleteSaved = async (sub, top) => {
    if (!window.confirm(`Delete saved content for "${sub}" – "${top}"?`)) return;
    const token = getToken();
    if (!token) { setError("You must be logged in to delete."); return; }
    try {
      const res = await fetch(
        `${getApiBase()}/lecture-notes/content?subject=${encodeURIComponent(sub.trim())}&topic=${encodeURIComponent(top.trim())}`,
        { method: "DELETE", headers: { Authorization: "Bearer " + token } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        setHistory(prev => prev.filter(item => !(item.subject === sub && item.topic === top)));
        if (result?.subject === sub && result?.topic === top) setResult(null);
      } else setError(formatApiError(data.error, "Delete failed"));
    } catch (err) { setError("Delete failed – " + err.message); }
  };

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "8px 4px 28px" }}>
      <PageHeader
        title="Lecture Notes"
        subtitle="Upload your syllabus — AI generates lecture notes, activities, worksheets, and assessments from your content."
        icon="📚"
        actions={(
          <button type="button" onClick={() => setShowHistory(!showHistory)}
            style={{ fontSize: 12, padding: "7px 14px", background: showHistory ? C.primary : C.primarySoft,
              color: showHistory ? "#fff" : C.primary, border: `1px solid ${C.primary}55`,
              borderRadius: 8, cursor: "pointer", fontWeight: 700 }}>
            {historyLoading ? "Loading..." : `Saved (${history.length})`}
          </button>
        )}
      />

      <div style={{
        marginBottom: 14, padding: "10px 14px", borderRadius: 10,
        background: C.primarySoft, border: `1px solid ${C.primary}22`,
        fontSize: 12.5, color: C.primaryDark, fontWeight: 600,
      }}>
        {plannerContextBanner(getPlannerContext())}
      </div>

      {/* History Panel */}
      {showHistory && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 10 }}>Previously Generated Content</div>
          {history.length === 0
            ? <div style={{ fontSize: 13, color: C.muted }}>No saved content yet.</div>
            : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {history.map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 14px", background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{item.subject}</div>
                      <div style={{ fontSize: 12, color: C.muted }}>{item.topic} · {new Date(item.updatedAt).toLocaleDateString()}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => loadSaved(item.subject, item.topic)}
                        style={{ fontSize: 12, padding: "6px 14px", background: C.primary, color: "#fff",
                          border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                        Load
                      </button>
                      <button onClick={() => deleteSaved(item.subject, item.topic)}
                        style={{ fontSize: 12, padding: "6px 14px", background: C.error, color: "#fff",
                          border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </div>
      )}

      {/* Input Form */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Subject *</label>
            <input value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="e.g. Data Structures, Operating Systems"
              style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Topic *</label>
            <input value={topic} onChange={e => setTopic(e.target.value)}
              placeholder="e.g. Binary Search Trees, Deadlock"
              style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
            Syllabus / Content Upload <span style={{ fontWeight: 400, color: C.muted }}>(PDF, DOCX, TXT)</span>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{ display: "inline-block", cursor: "pointer", padding: "9px 16px", background: "#E8F0FF",
              color: C.primary, border: "1px solid #3D7EFF", borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
              {uploading ? "Uploading..." : syllabusText ? "✓ Syllabus Loaded — Re-upload" : "📎 Upload Syllabus"}
              <input type="file" accept=".pdf,.txt,.docx" style={{ display: "none" }} onChange={e => handleUpload(e.target.files)} />
            </label>
            {syllabusText && (
              <span style={{ fontSize: 12, color: C.success }}>✅ {syllabusText.length.toLocaleString()} characters loaded</span>
            )}
          </div>
        </div>

        {/* Type selector */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 8 }}>What to Generate</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {TYPES.map(t => (
              <div key={t.key} onClick={() => setType(t.key)}
                style={{ padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                  border: `1.5px solid ${type === t.key ? C.primary : C.border}`,
                  background: type === t.key ? "#E8F0FF" : C.bg, transition: "all 0.1s" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: type === t.key ? C.primary : C.text }}>{t.label}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{t.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div style={{ padding: "8px 12px", background: C.errorBg, color: C.error,
            borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>
        )}

        <button onClick={handleGenerate} disabled={loading}
          style={{ background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "11px 24px",
            fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
          {loading ? "✨ Generating from your syllabus..." : "✨ Generate Content"}
        </button>
      </div>

      {/* ── Result ── */}
      {result && (
        <div>
          {/* Result header */}
          <div style={{ background: C.successBg, border: "1px solid #6EE7B7", borderRadius: 10,
            padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: C.success }}>
                ✅ {result.subject} — {result.topic}
              </div>
              <div style={{ fontSize: 12, color: "#065F46", marginTop: 2 }}>
                {[result.lectureNotes && "Lecture Notes", result.activity && "Activity",
                  result.worksheet && "Worksheet", result.assessment && "Assessment"]
                  .filter(Boolean).join(" · ")}
              </div>
            </div>
            <button onClick={() => downloadAsText(result)}
              style={{ fontSize: 12, padding: "7px 16px", background: C.success, color: "#fff",
                border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
              ⬇ Download .txt
            </button>
          </div>

          <LectureNotesSection notes={result.lectureNotes} />
          <ActivitySection     activity={result.activity} />
          <WorksheetSection    worksheet={result.worksheet} />
          <AssessmentSection   assessment={result.assessment} />
        </div>
      )}
    </div>
  );
}